import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { AppBar, Toolbar } from '@mui/material';
import { LeftMenu, AccountMenu } from './components';
import { listMenuAPI } from '../../apis/LeftMenuAPI';
import './Navbar.scss';
import { useSnackbar } from 'notistack';
import { navbarStyle } from './style';
import {UseCustomSnackbar} from '../../utils/CustomSnackbar';

export default function Navbar({
  keycloak,
  isAdmin,
  savatarURL,
  rooms,
  addRoom,
  deleteRoom,
  editRoom,
}) {
  const [itemsLeftMenu, setLeftMenu] = useState([]);
  const [leftMenuMobile, setLeftMenuMobile] = useState(false);
  const navbarRef = useRef();
  const leftMenuRef = useRef();
  const accountMenuRef = useRef();
  const leftMenuNaturalWidth = useRef(0);
  const classes = navbarStyle();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Measure the natural (single-line) width of the desktop menu by summing each
  // top-level <li> width (plus its horizontal margins). This stays accurate even if
  // the items would wrap, unlike the offsetWidth of the float-based container, which
  // collapses. No-op when the desktop menu is not mounted (collapsed state).
  const measureNaturalWidth = useCallback(() => {
    const menuList = leftMenuRef.current?.querySelector('ul');
    if (!menuList || !menuList.children.length) {return;}
    leftMenuNaturalWidth.current = Array.from(menuList.children).reduce((sum, li) => {
      const style = window.getComputedStyle(li);
      const marginX = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
      return sum + li.getBoundingClientRect().width + marginX;
    }, 0);
  }, []);

  // Decide between the full desktop menu and the collapsed hamburger by comparing
  // the menu's natural width with the space actually available between the start of
  // the toolbar content and the left edge of the account menu. Reading live layout
  // (instead of a fixed pixel breakpoint) keeps this correct for any menu configuration.
  const updateLayout = useCallback(() => {
    const natural = leftMenuNaturalWidth.current;
    if (!natural || !leftMenuRef.current || !accountMenuRef.current) {return;}
    const menuStart = leftMenuRef.current.getBoundingClientRect().left;
    const accountStart = accountMenuRef.current.getBoundingClientRect().left;
    const available = accountStart - menuStart;
    const SAFETY_GAP = 8; // keep the menu from touching the account cluster
    setLeftMenuMobile(natural + SAFETY_GAP > available);
  }, []);

  // Load the list data
  const loadList = async (enqueueSnackbar, closeSnackbar) => {
    try {
      const response = await listMenuAPI();
      setLeftMenu(response.data);
    } catch (error) {
      UseCustomSnackbar(enqueueSnackbar, closeSnackbar, error, 'error');
    }
  };

  useEffect(() => {
    loadList(enqueueSnackbar, closeSnackbar);
  }, [enqueueSnackbar, closeSnackbar]);

  // Re-measure whenever the desktop menu is mounted: initial load, a menu config
  // change, or coming back from the collapsed state after widening the window.
  // Measuring on every remount prevents a stale (e.g. pre-font-load) width from
  // lingering and causing the menu to wrap instead of collapsing. Runs before paint.
  useLayoutEffect(() => {
    if (!itemsLeftMenu.length || leftMenuMobile) {return;}
    measureNaturalWidth();
    updateLayout();
  }, [itemsLeftMenu, leftMenuMobile, measureNaturalWidth, updateLayout]);

  // Web fonts change glyph widths; a measurement taken before they load would
  // underestimate the menu width. Re-measure once fonts are ready.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (cancelled) {return;}
      measureNaturalWidth();
      updateLayout();
    });
    return () => { cancelled = true; };
  }, [measureNaturalWidth, updateLayout]);

  useEffect(() => {
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [updateLayout]);

  const onAccountMenuLoad = useCallback(() => {
    updateLayout();
  }, [updateLayout]);

  return (
    <div className={classes.grow}>
      <AppBar ref={navbarRef} className={classes.appBar} position="fixed" elevation={3}>
        <Toolbar>
          <div ref={leftMenuRef}>
            <LeftMenu items={itemsLeftMenu} mobile={leftMenuMobile} />
          </div>
          <div className={classes.grow} />
          <div ref={accountMenuRef}>
            <AccountMenu
              edge="end"
              fullKeycloak={keycloak}
              isAdmin={isAdmin}
              savatarURL={savatarURL}
              rooms={rooms}
              addRoom={addRoom}
              deleteRoom={deleteRoom}
              editRoom={editRoom}
              isDashboard={false}
              onLoad={onAccountMenuLoad}
            />
          </div>
        </Toolbar>
      </AppBar>
    </div>
  );
}
